import { Injectable } from '@angular/core';
import { MachineApiService } from './machine-api.service';

type ApiResponse<T> = { data: T; success: boolean };

export type DevopsIdentity = {
  authenticated: boolean;
  email: string | null;
  message?: string;
  checkedAt: number;
};

export type DevopsProxy = {
  id: string;
  spaceSlug: string;
  capsuleId: string;
  localPort: number;
  status: 'idle' | 'starting' | 'running' | 'stopping' | 'stopped' | 'failed';
  pid: number | null;
  startedAt: number;
  updatedAt: number;
  output: string[];
  stopReason?: string;
  error?: string;
};

export type DevopsPlatformIdentity = {
  authenticated: boolean;
  email: string | null;
  pendingTwoFactor?: boolean;
  message?: string;
  checkedAt: number;
};

export type DevopsEnvironment = 'dev' | 'staging' | 'prod' | 'other';

export type DevopsTeam = {
  id: string;
  slug: string;
  name: string;
  matched?: boolean;
  matchSource?: 'project-name' | 'repo-fallback';
  spaceCount?: number;
  capsuleCount?: number;
};

export type DevopsSpace = {
  id: string;
  slug: string;
  name: string;
  region?: string;
  capsuleCount?: number;
  environment?: DevopsEnvironment;
  matched?: boolean;
  matchedRepos?: string[];
  matchedCapsuleCount?: number;
  matchedCapsuleIds?: string[];
  teamId?: string;
  teamName?: string;
  teamSlug?: string;
  namespaceKey?: string;
  namespaceId?: string;
};

export type DevopsCapsule = {
  id: string;
  name: string;
  type?: string;
  status?: string;
  region?: string;
  repo?: string;
  url?: string;
};

export type DevopsBuild = {
  id: string;
  status: string;
  branch?: string;
  commit?: string;
  createdAt: number | null;
  updatedAt: number | null;
  message?: string;
};

export type DevopsBuildLog = {
  buildId: string;
  content: string;
  source: string;
  fetchedAt: number;
};

export type DevopsSpaceGroup = {
  environment: DevopsEnvironment;
  label: string;
  spaces: DevopsSpace[];
};

export type DevopsCapabilities = {
  auth: boolean;
  proxy: boolean;
  platform: boolean;
  deployments: boolean;
  builds: boolean;
  logs: boolean;
  incidents: boolean;
};

export type DevopsWorkspaceSelection = {
  teams: DevopsTeam[];
  selectedTeamId: string | null;
  selectedTeam: DevopsTeam | null;
  selectedSpaceId: string | null;
  selectedCapsuleId: string | null;
  selectedBuildId: string | null;
  spaces: DevopsSpace[];
  spaceGroups: DevopsSpaceGroup[];
  capsules: DevopsCapsule[];
  webDeployments: DevopsCapsule[];
  builds: DevopsBuild[];
  buildLog: DevopsBuildLog | null;
  incidents: any[];
  notes: string[];
  projectName: string | null;
  projectRepoCount: number;
  matchedSpaceCount: number;
  matchedCapsuleCount: number;
  showingFallbackSpaces: boolean;
};

export type DevopsOverview = DevopsWorkspaceSelection & {
  capabilities: DevopsCapabilities;
  identity: DevopsIdentity;
  platform: DevopsPlatformIdentity;
  proxy: DevopsProxy | null;
  checkedAt: number;
};

@Injectable({ providedIn: 'root' })
export class DevopsService {
  constructor(private api: MachineApiService) {}

  private async unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
    const res = await p;
    return res.data;
  }

  async overview(
    selection: {
      teamId?: string | null;
      spaceId?: string | null;
      capsuleId?: string | null;
      buildId?: string | null;
      projectName?: string | null;
      projectRepos?: string[];
    } = {}
  ): Promise<DevopsOverview> {
    const qs = new URLSearchParams();
    if (selection.teamId) qs.set('teamId', selection.teamId);
    if (selection.spaceId) qs.set('spaceId', selection.spaceId);
    if (selection.capsuleId) qs.set('capsuleId', selection.capsuleId);
    if (selection.buildId) qs.set('buildId', selection.buildId);
    if (selection.projectName) qs.set('projectName', selection.projectName);
    for (const projectRepo of selection.projectRepos ?? []) {
      const repo = String(projectRepo ?? '').trim();
      if (repo) qs.append('projectRepo', repo);
    }
    const suffix = qs.size ? `?${qs.toString()}` : '';
    return this.unwrap(this.api.request<ApiResponse<DevopsOverview>>(`/api/devops/overview${suffix}`));
  }

  async whoami(): Promise<DevopsIdentity> {
    return this.unwrap(this.api.request<ApiResponse<DevopsIdentity>>('/api/devops/codecapsules/whoami'));
  }

  async login(
    input: { email: string; password: string }
  ): Promise<{ ok: boolean; identity: DevopsIdentity; platform: DevopsPlatformIdentity; message: string }> {
    return this.unwrap(
      this.api.postJson<ApiResponse<{ ok: boolean; identity: DevopsIdentity; platform: DevopsPlatformIdentity; message: string }>>(
        '/api/devops/codecapsules/login',
        input
      )
    );
  }

  async logout(): Promise<{ ok: boolean; message: string }> {
    return this.unwrap(this.api.postJson<ApiResponse<{ ok: boolean; message: string }>>('/api/devops/codecapsules/logout', {}));
  }

  async verifyTwoFactor(code: string): Promise<{ ok: boolean; platform: DevopsPlatformIdentity; message: string }> {
    return this.unwrap(
      this.api.postJson<ApiResponse<{ ok: boolean; platform: DevopsPlatformIdentity; message: string }>>(
        '/api/devops/codecapsules/verify-2fa',
        { code }
      )
    );
  }

  async startProxy(input: { spaceSlug: string; capsuleId: string; localPort: number }): Promise<{ proxy: DevopsProxy }> {
    return this.unwrap(this.api.postJson<ApiResponse<{ proxy: DevopsProxy }>>('/api/devops/codecapsules/proxy/start', input));
  }

  async stopProxy(reason?: string): Promise<{ stopped: boolean; proxy: DevopsProxy | null }> {
    return this.unwrap(
      this.api.postJson<ApiResponse<{ stopped: boolean; proxy: DevopsProxy | null }>>('/api/devops/codecapsules/proxy/stop', {
        reason: reason ?? 'manual_stop',
      })
    );
  }

  async proxyStatus(): Promise<{ proxy: DevopsProxy | null }> {
    return this.unwrap(this.api.request<ApiResponse<{ proxy: DevopsProxy | null }>>('/api/devops/codecapsules/proxy/status'));
  }
}

